import React, { useState, useEffect, useCallback } from "react";
import {
    Button,
    Col,
    Form,
    Image,
    OverlayTrigger,
    Row,
    Tooltip,
} from "react-bootstrap";
import infoImg from "../../../../../../assets/infoimg.png";
import { countriesList } from "../../../../../../store/AllListData";
import GeneralInfoModal from "./GeneralInfoModal";
import { getAuthUserlocal } from "../../../../../../store/services";
import { FaEye, FaTrashAlt } from "react-icons/fa";

const industries = [
    "Agriculture",
    "Automotive & Heavy Industry",
    "Construction & Engineering",
    "Commodities Trading",
    "Energy & Power",
    "Fashion & Luxury Goods",
    "Gas",
    "High-tech Products",
    "Infrastructure",
    "Media & Entertainment",
    "Mining & Metals",
    "Oil & Petroleum",
    "Real Estate",
    "Pharmaceutical & Biotech",
    "Shipbuilding",
    "Telecommunications",
    "Transportation",
    "Logistics & Shipping",
    "Agri-Tech",
    "Machineries",
    "Other"
];

const ProfileOfClaimant = ({ formData, setFormData, showValidation }) => {
    const [showGeneralInfo, setShowGeneralInfo] = useState(false);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [uploadError, setUploadError] = useState("");
    const [isFormValid, setIsFormValid] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [showCustomIndustry, setShowCustomIndustry] = useState(false);
    const [customIndustry, setCustomIndustry] = useState("");
    const [memberType, setMemberType] = useState("Non Member");
    const [aecciRegNo, setAecciRegNo] = useState("");

    // Initialize uploadedFile from formData when component mounts or formData changes
    useEffect(() => {
        if (formData.companyRegistrationDoc) {
            setUploadedFile(formData.companyRegistrationDoc);
        }
    }, [formData.companyRegistrationDoc]);

    // Get user data and set member type and AECCI Reg No
    useEffect(() => {
        const user = getAuthUserlocal();
        if (user) {
            const regNo = user.memberShipNo || "";
            setAecciRegNo(regNo);

            let memberTypeValue = "Non Member";
            let filingFee = 400; // Default for non-members
            if (user.selectMembership && user.selectMembership !== "Digital User") {
                memberTypeValue = "AECCI Member";
                filingFee = 300; // For AECCI members
            }
            setMemberType(memberTypeValue);

            // Update formData with the new values
            setFormData(prev => ({
                ...prev,
                aecciRegNo: regNo,
                memberType: memberTypeValue,
                filingFee: filingFee
            }));
        }
    }, [setFormData]);

    // Validation functions
    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePhone = (phone) => {
        if (!phone) return false;
        return true;
    };

    const validatePAN = (pan) => {
        if (!pan) {
            return true;
        }
        const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
        return panRegex.test(pan);
    };

    const validateZipCode = (zip) => {
        if (!zip) return false;
        return true;
    };

    const validateField = useCallback((name, value) => {
        let error = "";
        switch (name) {
            case "email":
                if (!value) error = "Email is required";
                else if (!validateEmail(value)) error = "Invalid email format";
                break;
            case "phone":
                if (!value) error = "Phone number is required";
                else if (!validatePhone(value)) error = "Invalid phone number format";
                break;
            case "panNo":
                if (!validatePAN(value)) error = "Invalid PAN number format";
                break;
            case "zipCode":
                if (!value) error = "Zip code is required";
                else if (!validateZipCode(value)) error = "Zip code must be 6 digits";
                break;
            case "aecciRegNo":
                if (!value) error = "AECCI Reg No is required";
                break;
            case "memberType":
                if (!value) error = "Member type is required";
                break;
            default:
                if (!value) error = "This field is required";
                break;
        }
        return error;
    }, []);

    const handleInputChange = (e) => {
        const { name } = e.target;
        let { value } = e.target;
        if (name === "panNo") {
            value = value.toUpperCase();
        }
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        const error = validateField(name, value);
        setFieldErrors(prev => ({
            ...prev,
            [name]: error
        }));
    };

    // Validate all fields when showValidation changes
    useEffect(() => {
        if (showValidation) {
            const fields = [
                "date",
                "place",
                "claimantName",
                "respondentName",
                "address",
                "city",
                "country",
                "phone",
                "email",
                "natureOfBusiness",
                "industry",
                "aecciRegNo",
                "memberType",
                "panNo",
                "zipCode"
            ];

            const newErrors = {};
            fields.forEach(field => {
                newErrors[field] = validateField(field, formData[field]);
            });

            setFieldErrors(newErrors);
        }
    }, [showValidation, formData, validateField]);

    // Check if all required fields are filled
    useEffect(() => {
        const requiredFields = [
            "date",
            "place",
            "claimantName",
            "respondentName",
            "address",
            "city",
            "country",
            "phone",
            "email",
            "natureOfBusiness",
            "industry",
            "aecciRegNo",
            "memberType",
            "zipCode",
            "termsAccepted"
        ];

        const isAllFieldsFilled = requiredFields.every(field => {
            if (field === "termsAccepted") {
                return formData[field] === true;
            }
            return formData[field] && formData[field].trim() !== "";
        });

        const isFileUploaded = uploadedFile !== null;

        setIsFormValid(isAllFieldsFilled && isFileUploaded);
    }, [formData, uploadedFile]);

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        setUploadError("");

        if (!file) return;

        // Check file type
        const allowedTypes = [
            'application/pdf',
            'image/jpeg',
            'image/jpg',
            'image/png',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ];

        if (!allowedTypes.includes(file.type)) {
            setUploadError("Only PDF, images (JPG, PNG) and Word documents are allowed");
            return;
        }

        // Check file size (5MB = 5 * 1024 * 1024 bytes)
        if (file.size > 5 * 1024 * 1024) {
            setUploadError("File size should not exceed 5MB");
            return;
        }

        setUploadedFile(file);
        setFormData(prev => ({
            ...prev,
            companyRegistrationDoc: file
        }));
    };

    // Handle industry selection
    const handleIndustryChange = (e) => {
        const selectedValue = e.target.value;
        if (selectedValue === "Other") {
            setShowCustomIndustry(true);
            setFormData(prev => ({ ...prev, industry: customIndustry }));
        } else {
            setShowCustomIndustry(false);
            setCustomIndustry("");
            setFormData(prev => ({ ...prev, industry: selectedValue }));
        }
    };

    // Handle custom industry input
    const handleCustomIndustryChange = (e) => {
        const value = e.target.value;
        setCustomIndustry(value);
        setFormData(prev => ({ ...prev, industry: value }));
    };

    return (
        <>
            {/* Case Filling Detail Section */}
            <div className="border-3 rounded px-3 py-3" style={{ background: "#F4F4F9" }}>
                <h5 className="mb-3" style={{ color: "#2c3e50", fontWeight: "800" }}>
                    Case Filling Detail
                </h5>
                <Row className="mb-3">
                    <Col lg="6">
                        <Form.Group>
                            <Form.Label className="fw-semibold fs-6">
                                Member Type
                            </Form.Label>
                            <div className="d-flex gap-3 mt-2">
                                <label className="d-flex align-items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="member"
                                        name="memberType"
                                        checked={memberType === "AECCI Member"}
                                        disabled
                                        readOnly
                                        style={{ width: "16px", height: "16px" }}
                                    />
                                    <span className="fs-6">AECCI Member</span>
                                </label>
                                <label className="d-flex align-items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="nonMember"
                                        name="memberType"
                                        checked={memberType === "Non Member"}
                                        disabled
                                        readOnly
                                        style={{ width: "16px", height: "16px" }}
                                    />
                                    <span className="fs-6">Non Member</span>
                                </label>
                            </div>
                        </Form.Group>
                    </Col>
                    <Col lg="6">
                        <Form.Group>
                            <Form.Label className="fw-semibold fs-6 mb-1">
                                AECCI Reg No <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                value={aecciRegNo}
                                disabled
                                placeholder="AECCI Registration Number"
                                style={{ backgroundColor: "#f8f9fa" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>
            </div>

            <Row className="mb-3 mt-2 align-items-end">
                <Col>
                    <Row>
                        <Form.Group as={Col} lg="4" controlId="ShipmentFrom">
                            <Form.Label className="fw-semibold fs-6 mb-1">
                                Date <span className="text-danger">*</span>
                                <OverlayTrigger
                                    placement="right"
                                    overlay={
                                        <Tooltip id="button-tooltip-2">
                                            Enter the date when the form is being filled.
                                        </Tooltip>
                                    }
                                >
                                    {({ ref, ...triggerHandler }) => (
                                        <Button
                                            variant="light"
                                            {...triggerHandler}
                                            className="d-inline-flex align-items-center"
                                            style={{
                                                backgroundColor: "transparent",
                                                border: "none",
                                            }}
                                        >
                                            <Image
                                                ref={ref}
                                                roundedCircle
                                                src={infoImg}
                                                style={{ height: "3vh" }}
                                                alt=""
                                            />
                                        </Button>
                                    )}
                                </OverlayTrigger>
                            </Form.Label>
                            <Row>
                                <Col lg={12}>
                                    <Form.Group controlId="Date">
                                        <Form.Control
                                            type="date"
                                            name="date"
                                            onChange={handleInputChange}
                                            value={formData.date}
                                            required
                                            placeholder="date"
                                            isInvalid={showValidation && !!fieldErrors.date}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {fieldErrors.date}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Form.Group>
                    </Row>
                </Col>

                <Col lg="2">
                    <Row className="align-items-end">
                        <Button variant="info" size="sm" onClick={() => setShowGeneralInfo(true)}>
                            General Info
                        </Button>
                    </Row>
                </Col>
            </Row>

            <Row className="mb-3 mt-2 align-items-end">
                <Col lg="6">
                    <Row>
                        <Col lg="12">
                            <Form.Label className="fw-semibold fs-6 mb-1">
                                Place <span className="text-danger">*</span>
                                <OverlayTrigger
                                    placement="right"
                                    overlay={<Tooltip id="button-tooltip-2">place</Tooltip>}
                                >
                                    {({ ref, ...triggerHandler }) => (
                                        <Button
                                            variant="light"
                                            {...triggerHandler}
                                            className="d-inline-flex align-items-center"
                                            style={{
                                                backgroundColor: "transparent",
                                                border: "none",
                                            }}
                                        >
                                            <Image
                                                ref={ref}
                                                roundedCircle
                                                src={infoImg}
                                                style={{ height: "3vh" }}
                                                alt=""
                                            />
                                        </Button>
                                    )}
                                </OverlayTrigger>
                            </Form.Label>
                            <Form.Group controlId="Place">
                                <Form.Control
                                    type="text"
                                    onChange={handleInputChange}
                                    value={formData.place}
                                    name="place"
                                    maxLength={40}
                                    required
                                    placeholder="place"
                                    isInvalid={showValidation && !!fieldErrors.place}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {fieldErrors.place}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>
                </Col>

                <Col lg="6">
                    <Row className="">
                        <Col lg="12" className="d-flex justify-content-end">
                            <Button variant="info" size="sm" disabled>Time Table</Button>
                        </Col>
                    </Row>
                </Col>
            </Row>

            <div className="mb-3 d-flex align-items-end justify-content-center">
                <div className="flex-grow-1">
                    <Form.Label className="fw-semibold fs-6 mb-1">
                        Name of Claimant <span className="text-danger">*</span>
                        <OverlayTrigger
                            placement="right"
                            overlay={<Tooltip id="button-tooltip-2">Name of Claimant</Tooltip>}
                        >
                            {({ ref, ...triggerHandler }) => (
                                <Button
                                    variant="light"
                                    {...triggerHandler}
                                    className="d-inline-flex align-items-center"
                                    style={{ backgroundColor: "transparent", border: "none" }}
                                >
                                    <Image
                                        ref={ref}
                                        roundedCircle
                                        src={infoImg}
                                        style={{ height: "3vh" }}
                                        alt=""
                                    />
                                </Button>
                            )}
                        </OverlayTrigger>
                    </Form.Label>
                    <Form.Group controlId="claimantName">
                        <Form.Control
                            type="text"
                            onChange={handleInputChange}
                            value={formData.claimantName}
                            name="claimantName"
                            maxLength={60}
                            required
                            placeholder="Name of Claimant"
                            isInvalid={showValidation && !!fieldErrors.claimantName}
                        />
                        <Form.Control.Feedback type="invalid">
                            {fieldErrors.claimantName}
                        </Form.Control.Feedback>
                    </Form.Group>
                </div>
                <div className="text-center align-self-end mx-4" style={{ minWidth: "60px" }}>
                    <h6 className="mb-0">V/S</h6>
                </div>
                <div className="flex-grow-1">
                    <Form.Label className="fw-semibold fs-6 mb-1">
                        Name of Respondent <span className="text-danger">*</span>
                        <OverlayTrigger
                            placement="right"
                            overlay={<Tooltip id="button-tooltip-2">Name of Respondent</Tooltip>}
                        >
                            {({ ref, ...triggerHandler }) => (
                                <Button
                                    variant="light"
                                    {...triggerHandler}
                                    className="d-inline-flex align-items-center"
                                    style={{ backgroundColor: "transparent", border: "none" }}
                                >
                                    <Image
                                        ref={ref}
                                        roundedCircle
                                        src={infoImg}
                                        style={{ height: "3vh" }}
                                        alt=""
                                    />
                                </Button>
                            )}
                        </OverlayTrigger>
                    </Form.Label>
                    <Form.Group controlId="respondentName">
                        <Form.Control
                            type="text"
                            onChange={handleInputChange}
                            value={formData.respondentName}
                            name="respondentName"
                            maxLength={60}
                            required
                            placeholder="Name of Respondent"
                            isInvalid={showValidation && !!fieldErrors.respondentName}
                        />
                        <Form.Control.Feedback type="invalid">
                            {fieldErrors.respondentName}
                        </Form.Control.Feedback>
                    </Form.Group>
                </div>
            </div>

            <div className="border-3 rounded px-3 py-3" style={{ background: "#F4F4F9" }}>
                <Row className="mb-3">
                    <Form.Group as={Col} controlId="address">
                        <Form.Label className="fw-semibold fs-6 mb-1">
                            Address <span className="text-danger">*</span>
                            <OverlayTrigger
                                placement="right"
                                overlay={<Tooltip id="button-tooltip-2">address</Tooltip>}
                            >
                                {({ ref, ...triggerHandler }) => (
                                    <Button
                                        variant="light"
                                        {...triggerHandler}
                                        className="d-inline-flex align-items-center"
                                        style={{
                                            backgroundColor: "transparent",
                                            border: "none",
                                        }}
                                    >
                                        <Image
                                            ref={ref}
                                            roundedCircle
                                            src={infoImg}
                                            style={{ height: "3vh" }}
                                            alt=""
                                        />
                                    </Button>
                                )}
                            </OverlayTrigger>
                        </Form.Label>
                        <Form.Group controlId="address">
                            <Form.Control
                                type="text"
                                onChange={handleInputChange}
                                value={formData.address}
                                name="address"
                                maxLength={200}
                                required
                                placeholder=""
                                isInvalid={showValidation && !!fieldErrors.address}
                            />
                            <Form.Control.Feedback type="invalid">
                                {fieldErrors.address}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Form.Group>
                </Row>

                <Row className="mb-3">
                    <Form.Group as={Col} controlId="country">
                        <Form.Label className="fw-semibold fs-6 mb-1">
                            Country <span className="text-danger">*</span>
                            <OverlayTrigger
                                placement="right"
                                overlay={<Tooltip id="button-tooltip-2">Select the country</Tooltip>}
                            >
                                {({ ref, ...triggerHandler }) => (
                                    <Button
                                        variant="light"
                                        {...triggerHandler}
                                        className="d-inline-flex align-items-center"
                                        style={{
                                            backgroundColor: "transparent",
                                            border: "none",
                                        }}
                                    >
                                        <Image
                                            ref={ref}
                                            roundedCircle
                                            src={infoImg}
                                            style={{ height: "3vh" }}
                                            alt=""
                                        />
                                    </Button>
                                )}
                            </OverlayTrigger>
                        </Form.Label>
                        <Form.Select
                            value={formData.country}
                            onChange={handleInputChange}
                            name="country"
                            required
                            isInvalid={showValidation && !!fieldErrors.country}
                        >
                            <option value="">Select Country</option>
                            {countriesList.map((value, index) => (
                                <option key={index} value={value}>
                                    {value}
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            {fieldErrors.country}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group as={Col} controlId="city">
                        <Form.Label className="fw-semibold fs-6 mb-1">
                            City <span className="text-danger">*</span>
                            <OverlayTrigger
                                placement="right"
                                overlay={<Tooltip id="button-tooltip-2">Select the City</Tooltip>}
                            >
                                {({ ref, ...triggerHandler }) => (
                                    <Button
                                        variant="light"
                                        {...triggerHandler}
                                        className="d-inline-flex align-items-center"
                                        style={{
                                            backgroundColor: "transparent",
                                            border: "none",
                                        }}
                                    >
                                        <Image
                                            ref={ref}
                                            roundedCircle
                                            src={infoImg}
                                            style={{ height: "3vh" }}
                                            alt=""
                                        />
                                    </Button>
                                )}
                            </OverlayTrigger>
                        </Form.Label>
                        <Form.Group controlId="city">
                            <Form.Control
                                type="text"
                                onChange={handleInputChange}
                                value={formData.city}
                                name="city"
                                maxLength={40}
                                required
                                placeholder="city"
                                isInvalid={showValidation && !!fieldErrors.city}
                            />
                            <Form.Control.Feedback type="invalid">
                                {fieldErrors.city}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Form.Group>
                </Row>
                <Row className="mb-3">
                    <Form.Group as={Col} controlId="zipCode">
                        <Form.Label className="fw-semibold fs-6 mb-1">
                            Zip Code <span className="text-danger">*</span>
                            <OverlayTrigger
                                placement="right"
                                overlay={<Tooltip id="button-tooltip-2">zipcode</Tooltip>}
                            >
                                {({ ref, ...triggerHandler }) => (
                                    <Button
                                        variant="light"
                                        {...triggerHandler}
                                        className="d-inline-flex align-items-center"
                                        style={{
                                            backgroundColor: "transparent",
                                            border: "none",
                                        }}
                                    >
                                        <Image
                                            ref={ref}
                                            roundedCircle
                                            src={infoImg}
                                            style={{ height: "3vh" }}
                                            alt=""
                                        />
                                    </Button>
                                )}
                            </OverlayTrigger>
                        </Form.Label>
                        <Form.Group controlId="zipCode">
                            <Form.Control
                                type="text"
                                onChange={handleInputChange}
                                value={formData.zipCode}
                                name="zipCode"
                                minLength={6}
                                maxLength={12}                                
                                required
                                placeholder="zipcode"
                                isInvalid={showValidation && !!fieldErrors.zipCode}
                            />
                            <Form.Control.Feedback type="invalid">
                                {fieldErrors.zipCode}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Form.Group>

                    <Form.Group as={Col} controlId="phone">
                        <Form.Label className="fw-semibold fs-6 mb-1">
                            Phone <span className="text-danger">*</span>
                            <OverlayTrigger
                                placement="right"
                                overlay={<Tooltip id="button-tooltip-2">Phone</Tooltip>}
                            >
                                {({ ref, ...triggerHandler }) => (
                                    <Button
                                        variant="light"
                                        {...triggerHandler}
                                        className="d-inline-flex align-items-center"
                                        style={{
                                            backgroundColor: "transparent",
                                            border: "none",
                                        }}
                                    >
                                        <Image
                                            ref={ref}
                                            roundedCircle
                                            src={infoImg}
                                            style={{ height: "3vh" }}
                                            alt=""
                                        />
                                    </Button>
                                )}
                            </OverlayTrigger>
                        </Form.Label>
                        <Form.Group controlId="phone">
                            <Form.Control
                                type="text"
                                onChange={handleInputChange}
                                value={formData.phone}
                                name="phone"
                                maxLength={18}
                                minLength={10}
                                required
                                placeholder="phone"
                                isInvalid={showValidation && !!fieldErrors.phone}
                            />
                            <Form.Control.Feedback type="invalid">
                                {fieldErrors.phone}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Form.Group>
                </Row>

                <Row className="mb-3">
                    <Form.Group as={Col} controlId="panNo">
                        <Form.Label className="fw-semibold fs-6 mb-1">
                            PAN No
                            <OverlayTrigger
                                placement="right"
                                overlay={<Tooltip id="button-tooltip-2">PAN No</Tooltip>}
                            >
                                {({ ref, ...triggerHandler }) => (
                                    <Button
                                        variant="light"
                                        {...triggerHandler}
                                        className="d-inline-flex align-items-center"
                                        style={{
                                            backgroundColor: "transparent",
                                            border: "none",
                                        }}
                                    >
                                        <Image
                                            ref={ref}
                                            roundedCircle
                                            src={infoImg}
                                            style={{ height: "3vh" }}
                                            alt=""
                                        />
                                    </Button>
                                )}
                            </OverlayTrigger>
                        </Form.Label>
                        <Form.Group controlId="panNo">
                            <Form.Control
                                type="text"
                                onChange={handleInputChange}
                                value={formData.panNo}
                                name="panNo"
                                maxLength={10}
                                placeholder="ABCDE1234F"
                                isInvalid={showValidation && !!fieldErrors.panNo}
                            />
                            <Form.Control.Feedback type="invalid">
                                {fieldErrors.panNo}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Form.Group>
                    <Form.Group as={Col} controlId="email">
                        <Form.Label className="fw-semibold fs-6 mb-1">
                            Email id <span className="text-danger">*</span>
                            <OverlayTrigger
                                placement="right"
                                overlay={<Tooltip id="button-tooltip-2">email id</Tooltip>}
                            >
                                {({ ref, ...triggerHandler }) => (
                                    <Button
                                        variant="light"
                                        {...triggerHandler}
                                        className="d-inline-flex align-items-center"
                                        style={{
                                            backgroundColor: "transparent",
                                            border: "none",
                                        }}
                                    >
                                        <Image
                                            ref={ref}
                                            roundedCircle
                                            src={infoImg}
                                            style={{ height: "3vh" }}
                                            alt=""
                                        />
                                    </Button>
                                )}
                            </OverlayTrigger>
                        </Form.Label>
                        <Form.Group controlId="email">
                            <Form.Control
                                type="email"
                                onChange={handleInputChange}
                                value={formData.email}
                                name="email"
                                maxLength={40}
                                required
                                placeholder="email"
                                isInvalid={showValidation && !!fieldErrors.email}
                            />
                            <Form.Control.Feedback type="invalid">
                                {fieldErrors.email}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Form.Group>
                </Row>

                <Row>
                    <Form.Group as={Col} controlId="natureOfBusiness">
                        <Form.Label className="fw-semibold fs-6 mb-1">
                            Nature of Business <span className="text-danger">*</span>
                            <OverlayTrigger
                                placement="right"
                                overlay={<Tooltip id="button-tooltip-2">Nature of Business</Tooltip>}
                            >
                                {({ ref, ...triggerHandler }) => (
                                    <Button
                                        variant="light"
                                        {...triggerHandler}
                                        className="d-inline-flex align-items-center"
                                        style={{
                                            backgroundColor: "transparent",
                                            border: "none",
                                        }}
                                    >
                                        <Image
                                            ref={ref}
                                            roundedCircle
                                            src={infoImg}
                                            style={{ height: "3vh" }}
                                            alt=""
                                        />
                                    </Button>
                                )}
                            </OverlayTrigger>
                        </Form.Label>
                        <Form.Group controlId="natureOfBusiness">
                            <Form.Control
                                type="text"
                                onChange={handleInputChange}
                                value={formData.natureOfBusiness}
                                name="natureOfBusiness"
                                maxLength={80}
                                required
                                placeholder="Merchant exporter/Trading/Banking/Service Provider/Insurance/IT Sector....."
                                isInvalid={showValidation && !!fieldErrors.natureOfBusiness}
                            />
                            <Form.Control.Feedback type="invalid">
                                {fieldErrors.natureOfBusiness}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Form.Group>

                    <Form.Group as={Col} controlId="industry">
                        <Form.Label className="fw-semibold fs-6 mb-1">
                            Industry <span className="text-danger">*</span>
                            <OverlayTrigger
                                placement="right"
                                overlay={<Tooltip id="button-tooltip-2">Industry</Tooltip>}
                            >
                                {({ ref, ...triggerHandler }) => (
                                    <Button
                                        variant="light"
                                        {...triggerHandler}
                                        className="d-inline-flex align-items-center"
                                        style={{
                                            backgroundColor: "transparent",
                                            border: "none",
                                        }}
                                    >
                                        <Image
                                            ref={ref}
                                            roundedCircle
                                            src={infoImg}
                                            style={{ height: "3vh" }}
                                            alt=""
                                        />
                                    </Button>
                                )}
                            </OverlayTrigger>
                        </Form.Label>
                        <Form.Select
                            value={showCustomIndustry ? "Other" : (formData.industry || "")}
                            onChange={handleIndustryChange}
                            name="industry"
                            required
                            isInvalid={showValidation && !!fieldErrors.industry}
                        >
                            <option value="">Select</option>
                            {industries.map((value, index) => (
                                <option key={index} value={value}>
                                    {value}
                                </option>
                            ))}
                        </Form.Select>
                        {showCustomIndustry && (
                            <Form.Control
                                type="text"
                                className="mt-2"
                                placeholder="Enter your industry"
                                value={customIndustry}
                                onChange={handleCustomIndustryChange}
                                maxLength={50}
                                required
                                isInvalid={showValidation && !customIndustry}
                            />
                        )}
                        <Form.Control.Feedback type="invalid">
                            {fieldErrors.industry}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Row>

                <Row className="mb-3 mt-3 justify-content-center">
                    <Col lg="6">
                        <h5 className="">Upload Company Registration Document <span className="text-danger">*</span></h5>
                    </Col>
                    <Col lg="6">
                        <Form.Group>
                            <Form.Control
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                onChange={handleFileUpload}
                                style={{ display: "none" }}
                                id="company-registration-upload"
                            />
                            <Button
                                variant="success"
                                size="sm"
                                onClick={() => document.getElementById("company-registration-upload").click()}
                                className="me-2"
                            >
                                {uploadedFile ? "Change File" : "Upload"}
                            </Button>
                            {uploadedFile && (
                                <>
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={() => {
                                            const fileURL = URL.createObjectURL(uploadedFile);
                                            window.open(fileURL, '_blank');
                                        }}
                                        title="View uploaded file"
                                    >
                                        <FaEye />
                                    </Button>
                                    <Button
                                        variant="danger"
                                        size="sm"
                                        onClick={() => {
                                            setUploadedFile(null);
                                            setFormData(prev => ({ ...prev, companyRegistrationDoc: null }));
                                        }}
                                        className="ms-2"
                                    >
                                        <FaTrashAlt />
                                    </Button>
                                </>
                            )}
                            {uploadError && (
                                <div className="mt-2">
                                    <small className="text-danger">{uploadError}</small>
                                </div>
                            )}
                            {showValidation && !uploadedFile && (
                                <div className="mt-2">
                                    <small className="text-danger">Company registration document is required</small>
                                </div>
                            )}
                            <div className="mt-2">
                                <small className="text-danger">Max 5MB (PDF, JPG, PNG, DOC, DOCX)</small>
                            </div>
                        </Form.Group>
                    </Col>
                </Row>
            </div>

            <Form.Group className="mb-3 mt-3" id="formGridCheckbox">
                <div className="d-flex align-items-start gap-2">
                    <input
                        type="checkbox"
                        name="termsAccepted"
                        checked={formData.termsAccepted || false}
                        onChange={(e) => setFormData(prev => ({ ...prev, termsAccepted: e.target.checked }))}
                        style={{ width: "16px", height: "16px", marginTop: "2px" }}
                        required
                    />
                    <div>
                        <label className="mb-0" style={{ fontSize: "14px", lineHeight: "1.4" }}>
                            I hereby affirm that all the information provided herein is accurate and true to the best of my knowledge and I shall adhere to all the regulations of Asian Exporters Chamber of Commerce and Industry.
                        </label>
                        {showValidation && !formData.termsAccepted && (
                            <div className="text-danger mt-1 small">
                                You must accept the terms to proceed
                            </div>
                        )}
                    </div>
                </div>
            </Form.Group>

            {!isFormValid && (
                <div className="text-danger mt-3">
                    Please fill all required fields correctly before proceeding to the next step.
                </div>
            )}

            <GeneralInfoModal
                show={showGeneralInfo}
                handleClose={() => setShowGeneralInfo(false)}
            />
        </>
    );
};

export default ProfileOfClaimant;